<script lang="ts">
  import Modal from '@shared/ui/Modal.svelte';
  import {
    SALES_STATUS_LABELS,
    type SalesAction,
    type SalesStatus,
  } from '@shared/types/sales-action';

  interface SolutionOption {
    id: string;
    label: string;
  }

  interface Props {
    open: boolean;
    action: SalesAction;
    /// 進める先の商談以降ステータス (negotiation_c/b/a)。
    targetStatus: SalesStatus;
    solutions: SolutionOption[];
    /// 同じターゲットで既に open 商談行があるソリューション (選択不可 = 既存商談あり)。
    existingOpenSolutionIds: string[];
    /// 確定。 1 件 = 単一昇格、 複数 = ソリューションごとに行分割。
    onConfirm: (solutionIds: string[]) => void;
    onClose: () => void;
  }

  let {
    open,
    action,
    targetStatus,
    solutions,
    existingOpenSolutionIds,
    onConfirm,
    onClose,
  }: Props = $props();

  // ダイアログは対象ごとに再マウントされるため、 const 初期化で十分 (solutions は安定)。
  const labelById: Record<string, string> = Object.fromEntries(
    solutions.map((s) => [s.id, s.label]),
  );

  // 候補が 2 件以上ならその中から単一選択 or 分割。 候補なしなら全ソリューションから単一選択。
  const candidateIds = action.candidate_product_ids;
  const hasCandidates = candidateIds.length >= 2;
  const options: SolutionOption[] = hasCandidates
    ? candidateIds.map((id) => ({ id, label: labelById[id] ?? id }))
    : solutions;
  // 同じターゲットで既に open 商談行があるソリューションは選択不可。 重複商談を作らず、
  // 元行の昇格でも (target, solution) open 一意制約に衝突させない。
  function isExisting(id: string): boolean {
    return existingOpenSolutionIds.includes(id);
  }
  const selectableIds = options.filter((o) => !isExisting(o.id)).map((o) => o.id);
  const allExisting = selectableIds.length === 0;

  type Mode = 'single' | 'split';
  let mode = $state<Mode>('single');
  let singleId = $state(selectableIds[0] ?? '');
  let splitIds = $state<string[]>(candidateIds.filter((id) => !isExisting(id)));

  function toggleSplit(id: string) {
    if (isExisting(id)) return;
    splitIds = splitIds.includes(id)
      ? splitIds.filter((x) => x !== id)
      : [...splitIds, id];
  }

  const selectedIds = $derived(
    mode === 'single'
      ? singleId
        ? [singleId]
        : []
      : splitIds.filter((id) => !isExisting(id)),
  );
  const canConfirm = $derived(selectedIds.length >= 1);
  const confirmLabel = $derived(
    mode === 'split' && selectedIds.length > 1 ? '分割して進める' : '進める',
  );
</script>

<Modal {open} title="商談化するソリューションを選択" onClose={onClose}>
  <div class="body">
    <p>
      <strong>{action.action_title ?? '(無題)'}</strong> を
      <strong>{SALES_STATUS_LABELS[targetStatus]}</strong> へ進めます。商談以降は
      <strong>1ソリューション1行</strong> で管理します。
    </p>

    {#if hasCandidates}
      <div class="mode" role="radiogroup" aria-label="進め方">
        <label class="radio">
          <input
            type="radio"
            name="deal-mode"
            value="single"
            checked={mode === 'single'}
            onchange={() => (mode = 'single')}
          />
          <span>1つに絞る</span>
        </label>
        <label class="radio">
          <input
            type="radio"
            name="deal-mode"
            value="split"
            checked={mode === 'split'}
            onchange={() => (mode = 'split')}
          />
          <span>ソリューションごとに行を分割</span>
        </label>
      </div>
    {:else}
      <p class="muted">
        この行には候補ソリューションがありません。商談化するソリューションを選択してください。
      </p>
    {/if}

    {#if mode === 'single'}
      <label>
        <span>ソリューション <em>*</em></span>
        <select bind:value={singleId} data-testid="deal-single-select">
          <option value="">選択してください</option>
          {#each options as o (o.id)}
            <option value={o.id} disabled={isExisting(o.id)}>
              {o.label}{isExisting(o.id) ? '（既存商談あり）' : ''}
            </option>
          {/each}
        </select>
      </label>
    {:else}
      <fieldset>
        <legend>分割するソリューション（1 件以上）</legend>
        <div class="checks">
          {#each options as o (o.id)}
            <label class="check" class:disabled={isExisting(o.id)}>
              <input
                type="checkbox"
                checked={splitIds.includes(o.id)}
                disabled={isExisting(o.id)}
                onchange={() => toggleSplit(o.id)}
              />
              <span>
                {o.label}{#if isExisting(o.id)}
                  <span class="tag">既存商談あり</span>
                {/if}
              </span>
            </label>
          {/each}
        </div>
      </fieldset>
      <p class="muted" data-testid="deal-split-count">
        {selectedIds.length} 件の商談行を作成します（1ソリューション1行）。
      </p>
    {/if}

    {#if allExisting}
      <p class="warn">
        選択できるソリューションがありません（候補はすべて既存商談があります）。
      </p>
    {:else if !canConfirm}
      <p class="warn">商談化するソリューションを選択してください。</p>
    {/if}
  </div>
  {#snippet footer()}
    <button type="button" onclick={onClose}>キャンセル</button>
    <button
      type="button"
      class="primary"
      data-testid="deal-confirm"
      disabled={!canConfirm}
      onclick={() => onConfirm(selectedIds)}
    >
      {confirmLabel}
    </button>
  {/snippet}
</Modal>

<style>
  .body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .body p {
    margin: 0;
  }
  .mode {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
  }
  .radio,
  .check {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
  }
  .check.disabled {
    color: #9ca3af;
  }
  .tag {
    display: inline-block;
    margin-left: 4px;
    padding: 0 6px;
    font-size: 0.7rem;
    background: #f3f4f6;
    color: #6b7280;
    border-radius: 999px;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  label.radio,
  label.check {
    flex-direction: row;
  }
  span em {
    color: #b91c1c;
    font-style: normal;
  }
  select {
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    background: white;
  }
  fieldset {
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 8px 10px;
    margin: 0;
  }
  legend {
    font-size: 0.8rem;
    color: #374151;
    padding: 0 4px;
  }
  .checks {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
  }
  .muted {
    color: #6b7280;
    font-size: 0.875rem;
  }
  .warn {
    color: #b45309;
    font-size: 0.875rem;
  }
  button {
    padding: 8px 16px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
  }
  button.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: white;
  }
  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
</style>
